import { COMMUNICATION_CHART_ARROW_LENGTH_TO_WINGSPAN_RATIO, resolveCommunicationChartArrowWidthPx } from '@web/components/CommunicationNetworkGraph/graph/communicationChartConstants';
var asEdgeWithCurveHelpers = function (edge) {
    return edge;
};
var MIN_SAMPLE_DISTANCE_PX = 0.5;
var TANGENT_SAMPLE_NEAR_TARGET = 0.97;
var TANGENT_SAMPLE_NEAR_SOURCE = 0.03;
var isFiniteCommunicationChartPosition = function (position) {
    return position !== undefined &&
        position !== null &&
        Number.isFinite(position.x) &&
        Number.isFinite(position.y);
};
export { isFiniteCommunicationChartPosition };
export var tangentAngleFromInnerPointToEndpoint = function (endpoint, innerPoint) {
    if (!isFiniteCommunicationChartPosition(endpoint) ||
        !isFiniteCommunicationChartPosition(innerPoint)) {
        return null;
    }
    var dx = endpoint.x - innerPoint.x;
    var dy = endpoint.y - innerPoint.y;
    if (Math.hypot(dx, dy) < MIN_SAMPLE_DISTANCE_PX) {
        return null;
    }
    return Math.atan2(dy, dx);
};
export var getRenderedCommunicationChartNodeRadiusPx = function (node) {
    var _a;
    var renderedWidth = node.renderedWidth();
    if (Number.isFinite(renderedWidth) && renderedWidth > 0) {
        return renderedWidth / 2;
    }
    return Math.max(Number((_a = node.data('nodeSize')) !== null && _a !== void 0 ? _a : 0) / 2, 0);
};
var readRenderedPointAlongEdge = function (edge, relativePosition) {
    try {
        var point = asEdgeWithCurveHelpers(edge).renderedPointAlongEdge(relativePosition);
        if (Number.isFinite(point.x) && Number.isFinite(point.y)) {
            return point;
        }
    }
    catch (_a) {
        return null;
    }
    return null;
};
export var resolveEdgeTangentAngleAtEndpoint = function (edge, side, endpoint) {
    if (!isFiniteCommunicationChartPosition(endpoint)) {
        return null;
    }
    var innerPoint = readRenderedPointAlongEdge(edge, side === 'target' ? TANGENT_SAMPLE_NEAR_TARGET : TANGENT_SAMPLE_NEAR_SOURCE);
    if (innerPoint) {
        var tangentAngle = tangentAngleFromInnerPointToEndpoint(endpoint, innerPoint);
        if (tangentAngle !== null) {
            return tangentAngle;
        }
    }
    var node = side === 'target' ? edge.target() : edge.source();
    var center = node.renderedPosition();
    if (!isFiniteCommunicationChartPosition(center)) {
        return null;
    }
    return tangentAngleFromInnerPointToEndpoint(center, endpoint);
};
/** First forward intersection of a ray with the node circle. */
export var intersectRayWithNodePerimeter = function (origin, angle, center, radius) {
    if (radius <= 0) {
        return null;
    }
    var ux = Math.cos(angle);
    var uy = Math.sin(angle);
    var fx = origin.x - center.x;
    var fy = origin.y - center.y;
    var b = 2 * (fx * ux + fy * uy);
    var c = fx * fx + fy * fy - radius * radius;
    var discriminant = b * b - 4 * c;
    if (discriminant < 0) {
        return null;
    }
    var sqrtDiscriminant = Math.sqrt(discriminant);
    var candidates = [-b - sqrtDiscriminant, -b + sqrtDiscriminant]
        .map(function (value) { return value / 2; })
        .filter(function (distancePx) { return distancePx > MIN_SAMPLE_DISTANCE_PX; })
        .sort(function (left, right) { return left - right; });
    var distancePx = candidates[0];
    if (distancePx === undefined) {
        return null;
    }
    return {
        tip: {
            x: origin.x + ux * distancePx,
            y: origin.y + uy * distancePx
        },
        distancePx: distancePx
    };
};
export var resolveCommunicationChartArrowDepthPx = function (arrowSize, lineWidthPx) {
    if (!arrowSize || arrowSize === 'none' || lineWidthPx <= 0) {
        return 0;
    }
    return resolveCommunicationChartArrowWidthPx(arrowSize, lineWidthPx);
};
/**
 * Anchor the arrow base on the cytoscape line end, aim along the edge tangent,
 * and reach the node perimeter without overlapping the stroke.
 */
export var resolveCommunicationChartArrowPlacementAtNode = function (edge, node, side, depthPx) {
    if (depthPx <= 0) {
        return null;
    }
    var base;
    try {
        base =
            side === 'target'
                ? edge.renderedTargetEndpoint()
                : edge.renderedSourceEndpoint();
    }
    catch (_a) {
        return null;
    }
    if (!isFiniteCommunicationChartPosition(base)) {
        return null;
    }
    var angle = resolveEdgeTangentAngleAtEndpoint(edge, side, base);
    if (angle === null) {
        return null;
    }
    var tip = {
        x: base.x + Math.cos(angle) * depthPx,
        y: base.y + Math.sin(angle) * depthPx
    };
    return {
        tip: tip,
        base: base,
        angle: angle,
        depthPx: depthPx
    };
};
/** Standard triangular arrowhead (rear edge at x = -depthPx). */
export var drawCommunicationChartArrowHead = function (ctx, tip, angle, depthPx, color, opacity) {
    var halfWidth = depthPx / COMMUNICATION_CHART_ARROW_LENGTH_TO_WINGSPAN_RATIO / 2;
    ctx.save();
    ctx.translate(tip.x, tip.y);
    ctx.rotate(angle);
    ctx.globalAlpha = opacity;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(-depthPx, -halfWidth);
    ctx.lineTo(-depthPx, halfWidth);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
};
